import React from "react";
import {
  Plane,
  CreditCard,
  Search,
  CheckCircle,
  Star,
  Filter,
  ShieldCheck,
  Zap,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Calendar,
  Users,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import HowItWorks from "./HowItWorks";
import { useNavigate } from "react-router-dom";
import ComingSoonPage from "../Home/ComingSoonPage";

const RefinedLandingPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <section className="relative overflow-hidden bg-[#020817] py-24 px-6 lg:px-16 min-h-screen flex items-center">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-20 w-72 h-72 bg-cyan-400/10 blur-3xl rounded-full animate-blob"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-400/10 blur-3xl rounded-full animate-blob"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
          <svg
            className="absolute top-0 left-0 w-full h-full opacity-30"
            viewBox="0 0 1400 800"
            fill="none"
          >
            <path
              d="M100 500 C300 200, 900 150, 1250 350"
              stroke="#D4AF37"
              strokeWidth="2"
              strokeDasharray="10 10"
              className="animate-route"
            />
            <g className="animate-plane">
              <text x="0" y="0" fontSize="28">
                ✈️
              </text>
            </g>
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-7 gap-12 items-center">
          {/* LEFT SIDE */}
          <div className="lg:col-span-4">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md text-[11px] font-black uppercase tracking-[0.2em] text-[#D4AF37] mb-8">
              <Sparkles size={14} />
              Travel Rewards Platform
            </div>

            {/* Heading */}
            <h1 className="text-5xl lg:text-7xl font-semibold text-white leading-[0.95] tracking-tight">
              {" "}
              Make Smarter Use of Your
              <span className="block text-[#D4AF37]">
                Travel Rewards Points
              </span>
            </h1>

            {/* Subtext */}
            <p className="text-slate-300 text-lg leading-relaxed mt-8 max-w-2xl">
              Compare airline and credit card reward options in one place.
              Explore redemption possibilities across multiple loyalty programs
              and understand how your points can be used for travel.
            </p>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="group bg-[#D4AF37] hover:bg-yellow-400 transition-all text-black font-black px-8 py-4 rounded-2xl flex items-center justify-center gap-2 shadow-2xl shadow-yellow-500/20"
              >
                Get Started
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-all"
                />
              </button>

              <button
                type="button"
                onClick={() => navigate("/redemption-opportunities")}
                className="border border-white/10 bg-white/5 hover:bg-white/10 backdrop-blur-md transition-all text-white font-black px-8 py-4 rounded-2xl"
              >
                Explore Options
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
              {" "}
              <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-3xl p-5">
                <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-black">
                  Better Insights
                </p>
                <h3 className="text-3xl font-black text-white mt-2">
                  Comparison
                </h3>
              </div>
              <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-3xl p-5">
                <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-black">
                  Programs
                </p>
                <h3 className="text-3xl font-black text-white mt-2">25+</h3>
              </div>
              <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-3xl p-5">
                <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-black">
                  Options
                </p>
                <h3 className="text-3xl font-black text-white mt-2">
                  Multiple
                </h3>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="lg:col-span-3 relative">
            {/* TOP CARD */}
            <div className="bg-white rounded-[2rem] p-6 shadow-[0_20px_80px_rgba(0,0,0,0.35)] border border-slate-100 relative z-20 animate-float">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-black">
                    Top Redemption Example
                  </p>
                  <h3 className="text-xl font-black text-slate-900 mt-1">
                    New York → London
                  </h3>
                </div>

                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp size={20} className="text-emerald-600" />
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden h-44 relative">
                <img
                  src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=1200&auto=format&fit=crop"
                  alt=""
                  className="w-full h-full object-cover opacity-70"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>

                <div className="absolute bottom-5 left-5">
                  <p className="text-white text-sm font-bold">
                    Business Class Example
                  </p>

                  <h2 className="text-4xl font-black text-[#D4AF37] mt-1">
                    Estimated Value
                  </h2>

                  <p className="text-slate-300 text-xs mt-1">
                    Based on reward comparison across programs
                  </p>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-black text-emerald-600">
                    RECOMMENDED OPTION
                  </p>

                  <p className="text-xs text-slate-400 mt-1">
                    Optimized redemption example
                  </p>
                </div>

                <div className="w-12 h-12 rounded-2xl bg-emerald-500 text-white flex items-center justify-center font-black">
                  92
                </div>
              </div>
            </div>

            {/* LOWER CARD */}
            <div className="bg-[#0f172a] border border-white/10 backdrop-blur-xl rounded-[2rem] p-6 mt-6 lg:-ml-12 relative z-10">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-red-400 font-black mb-5">
                <Star size={12} />
                Lower Value Example
              </div>

              <div className="flex items-end gap-3">
                <h2 className="text-4xl font-black text-white">
                  Comparison Case
                </h2>

                <p className="text-red-400 text-sm font-bold mb-1">
                  Lower Value Option
                </p>
              </div>

              <p className="text-slate-400 text-sm mt-3 leading-relaxed">
                Some redemption methods may offer lower value compared to
                travel-based options. Comparing options can help improve your
                decisions.
              </p>

              <div className="mt-6">
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full w-[35%] bg-red-500 rounded-full"></div>
                </div>

                <div className="flex justify-between mt-2 text-[11px] font-bold text-slate-500">
                  <span>Lower Value</span>
                  <span>Comparison View</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HowItWorks />
      {/* <ComingSoonPage /> */}
    </>
  );
};

export default RefinedLandingPage;
